/**
 * Kuvio Console v3 — Primitives
 *
 * Reference React + TypeScript components baking the design rules in.
 * Every component reads from tokens.css and obeys the rules in design-rules.md.
 *
 * This file is the source-of-truth template. Copy into the Kuvio repo at
 * (suggested) `kuvio/console/src/v3/primitives.tsx` when implementing Sprint 0.
 *
 * Conventions:
 *   - Inline styles using CSS variable references (no CSS-in-JS dependency).
 *   - Tabler outline icons via webfont (<i class="ti ti-NAME" />).
 *   - Two font weights only: 400 default, 500 for headings/labels/primary buttons.
 *   - Sentence case strings throughout.
 *   - No emoji. No gradients. No shadows except focus ring.
 *
 * Companion files:
 *   - tokens.css        — CSS custom properties this file references.
 *   - design-rules.md   — The inviolable design checklist.
 *   - console-v3-spec.md — Where each primitive is used.
 *
 * Last updated: 2026-05-28
 */

import React, { useState, useRef, useEffect } from "react";
import type { ReactNode, CSSProperties } from "react";
// Sprint 16f — tool-list-at-scale topology guidance for <ConnectButton>.
import type { AttachmentPlan } from "../lib/attachment-recommender";

// ============================================================================
// Types
// ============================================================================

export type Persona =
  | "purple"
  | "teal"
  | "coral"
  | "pink"
  | "gray"
  | "blue"
  | "green"
  | "amber"
  | "red";

export type Surface =
  | "cowork"
  | "claude-web"
  | "claude"
  | "cursor"
  | "claude-code"
  | "chatgpt"
  | "background"
  | "scheduled";

export type Status = "active" | "idle" | "scheduled" | "blocked";

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";

export type AgentRef = {
  id: string;
  name: string;
  role?: string;
  persona: Persona;
  initials?: string; // optional override; otherwise derived from name
};

export type BreadcrumbSegment = {
  label: string;
  href?: string;
  onClick?: () => void;
};

// ============================================================================
// Internal helpers
// ============================================================================

// RC1-1 — exported so the ONE shared identity-colour util (lib/agent-persona.ts)
// and the raw-avatar workflow surfaces read their fill from the same table the
// <Avatar> component uses. Do not fork this map.
export const PERSONA_FILL: Record<Persona, string> = {
  purple: "var(--c-purple-600)",
  teal: "var(--c-teal-600)",
  coral: "var(--c-coral-400)",
  pink: "var(--c-pink-600)",
  gray: "var(--c-gray-600)",
  blue: "var(--c-blue-600)",
  green: "var(--c-green-600)",
  amber: "var(--c-amber-400)",
  red: "var(--c-red-600)",
};

const AVATAR_PX: Record<AvatarSize, { box: number; font: number }> = {
  xs: { box: 18, font: 9 },
  sm: { box: 22, font: 9 },
  md: { box: 26, font: 10 },
  lg: { box: 32, font: 11 },
  xl: { box: 52, font: 16 },
};

const SURFACE_META: Record<
  Surface,
  { label: string; icon: string; bgVar: string; fgVar: string }
> = {
  cowork: {
    label: "Cowork",
    icon: "ti-message",
    bgVar: "var(--kuvio-surface-claude-bg)",
    fgVar: "var(--kuvio-surface-claude-fg)",
  },
  "claude-web": {
    label: "Claude.ai",
    icon: "ti-world",
    bgVar: "var(--kuvio-surface-claude-bg)",
    fgVar: "var(--kuvio-surface-claude-fg)",
  },
  claude: {
    label: "Claude",
    icon: "ti-message",
    bgVar: "var(--kuvio-surface-claude-bg)",
    fgVar: "var(--kuvio-surface-claude-fg)",
  },
  cursor: {
    label: "Cursor",
    icon: "ti-code",
    bgVar: "var(--kuvio-surface-cursor-bg)",
    fgVar: "var(--kuvio-surface-cursor-fg)",
  },
  "claude-code": {
    label: "Claude Code",
    icon: "ti-terminal-2",
    bgVar: "var(--kuvio-surface-code-bg)",
    fgVar: "var(--kuvio-surface-code-fg)",
  },
  chatgpt: {
    label: "ChatGPT",
    icon: "ti-brand-openai",
    bgVar: "var(--kuvio-surface-chatgpt-bg)",
    fgVar: "var(--kuvio-surface-chatgpt-fg)",
  },
  background: {
    label: "Background",
    icon: "ti-robot",
    bgVar: "var(--kuvio-surface-bg-bg)",
    fgVar: "var(--kuvio-surface-bg-fg)",
  },
  scheduled: {
    label: "Scheduled",
    icon: "ti-clock",
    bgVar: "var(--c-amber-50)",
    fgVar: "var(--c-amber-800)",
  },
};

const STATUS_META: Record<Status, { color: string; defaultLabel: string }> = {
  active: { color: "var(--kuvio-status-active)", defaultLabel: "active" },
  idle: { color: "var(--kuvio-status-idle)", defaultLabel: "idle" },
  scheduled: {
    color: "var(--kuvio-status-scheduled)",
    defaultLabel: "scheduled",
  },
  blocked: { color: "var(--kuvio-status-blocked)", defaultLabel: "blocked" },
};

function deriveInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "";
  if (parts.length === 1) return (parts[0] ?? "").slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}

// ============================================================================
// <Avatar>
// ============================================================================

export type AvatarShape = "circle" | "rounded-rect";

export interface AvatarProps {
  persona: Persona;
  size?: AvatarSize;
  shape?: AvatarShape;
  initials?: string;
  name?: string; // used to derive initials if `initials` not provided
  imageUrl?: string; // optional AI-generated avatar (Sprint 3.4)
  title?: string;
}

export function Avatar({
  persona,
  size = "md",
  shape = "circle",
  initials,
  name,
  imageUrl,
  title,
}: AvatarProps) {
  const { box, font } = AVATAR_PX[size];
  const fill = PERSONA_FILL[persona];
  const text = initials ?? (name ? deriveInitials(name) : "");

  const style: CSSProperties = {
    width: box,
    height: box,
    borderRadius: shape === "rounded-rect" ? "var(--border-radius-md)" : "50%",
    background: fill,
    color: "#FFFFFF",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: font,
    fontWeight: 500,
    fontFamily: "var(--font-sans)",
    flexShrink: 0,
    overflow: "hidden",
    lineHeight: 1,
  };

  return (
    <span
      style={style}
      title={title ?? name}
      role="img"
      aria-label={name ?? text}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        text
      )}
    </span>
  );
}

// ============================================================================
// <NodeAvatar> — Sprint 5u
// ============================================================================
//
// One avatar that carries a library node's TYPE in its shape, so the rail and
// cockpit stay legible: agents are circular initials avatars (a persona you can
// "act as"); everything else is a rounded-square glyph (an organisational
// container). Reuses <Avatar> for the agent case; renders a Tabler webfont
// glyph for the rest.

export type NodeKind =
  | "agent"
  | "folder"
  | "table"
  | "page"
  | "file"
  | "memory"
  | "row"
  | "view"
  | "prompt";

const NODE_AVATAR_META: Record<NodeKind, { shape: AvatarShape; icon: string | null; persona: Persona }> = {
  agent: { shape: "circle", icon: null, persona: "purple" }, // initials; persona overridable
  folder: { shape: "rounded-rect", icon: "ti-folder", persona: "gray" },
  table: { shape: "rounded-rect", icon: "ti-table", persona: "blue" },
  page: { shape: "rounded-rect", icon: "ti-file-text", persona: "teal" },
  file: { shape: "rounded-rect", icon: "ti-paperclip", persona: "gray" },
  memory: { shape: "rounded-rect", icon: "ti-bulb", persona: "amber" },
  row: { shape: "rounded-rect", icon: "ti-list-details", persona: "gray" },
  view: { shape: "rounded-rect", icon: "ti-layout-grid", persona: "gray" },
  prompt: { shape: "rounded-rect", icon: "ti-bolt", persona: "amber" },
};

export interface NodeAvatarProps {
  type: NodeKind | string;
  name?: string;
  size?: AvatarSize;
  /** Override the type's default colour (e.g. an agent's brand persona). */
  persona?: Persona;
  initials?: string;
  title?: string;
}

export function NodeAvatar({ type, name, size = "md", persona, initials, title }: NodeAvatarProps) {
  const meta = NODE_AVATAR_META[type as NodeKind] ?? NODE_AVATAR_META.folder;

  // Agents: the existing circular initials avatar, in their brand colour.
  if (meta.icon === null) {
    return (
      <Avatar
        persona={persona ?? meta.persona}
        size={size}
        shape={meta.shape}
        name={name}
        initials={initials}
        title={title ?? name}
      />
    );
  }

  // Folders / tables / pages / files / memories: a rounded-square glyph box.
  const { box } = AVATAR_PX[size];
  const fill = PERSONA_FILL[persona ?? meta.persona];
  return (
    <span
      style={{
        width: box,
        height: box,
        borderRadius: "var(--border-radius-md)",
        background: fill,
        color: "#FFFFFF",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: Math.round(box * 0.52),
        flexShrink: 0,
        lineHeight: 1,
      }}
      title={title ?? name}
      role="img"
      aria-label={name ? `${type}: ${name}` : type}
    >
      <i className={`ti ${meta.icon}`} aria-hidden="true" />
    </span>
  );
}

// ============================================================================
// <SurfaceBadge>
// ============================================================================

export interface SurfaceBadgeProps {
  surface: Surface;
}

export function SurfaceBadge({ surface }: SurfaceBadgeProps) {
  const meta = SURFACE_META[surface];

  const style: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    fontSize: 10,
    fontWeight: 500,
    fontFamily: "var(--font-sans)",
    background: meta.bgVar,
    color: meta.fgVar,
    padding: "1px 6px",
    borderRadius: "var(--kuvio-badge-radius)",
    lineHeight: 1.4,
  };

  return (
    <span style={style}>
      <i
        className={`ti ${meta.icon}`}
        style={{ fontSize: 10, verticalAlign: -1 }}
        aria-hidden="true"
      />
      {meta.label}
    </span>
  );
}

// ============================================================================
// <StatusDot>
// ============================================================================

export interface StatusDotProps {
  status: Status;
  label?: string;
  pulse?: boolean;
}

export function StatusDot({ status, label, pulse = false }: StatusDotProps) {
  const meta = STATUS_META[status];
  const displayLabel = label ?? meta.defaultLabel;

  const dotStyle: CSSProperties = {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: meta.color,
    flexShrink: 0,
  };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontFamily: "var(--font-sans)",
      }}
    >
      <span
        style={dotStyle}
        className={
          pulse && status === "active" ? "kuvio-status-active-pulse" : undefined
        }
      />
      <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>
        {displayLabel}
      </span>
    </span>
  );
}

// ============================================================================
// <Pill>
// ============================================================================

export interface PillProps {
  children: ReactNode;
  variant?: Persona;
  icon?: string; // Tabler icon name without "ti-" prefix
  onClick?: () => void;
}

export function Pill({ children, variant = "gray", icon, onClick }: PillProps) {
  const baseFamily: Record<Persona, { bg: string; fg: string }> = {
    purple: { bg: "var(--c-purple-50)", fg: "var(--c-purple-800)" },
    teal: { bg: "var(--c-teal-50)", fg: "var(--c-teal-800)" },
    coral: { bg: "var(--c-coral-50)", fg: "var(--c-coral-800)" },
    pink: { bg: "var(--c-pink-50)", fg: "var(--c-pink-800)" },
    gray: {
      bg: "var(--color-background-secondary)",
      fg: "var(--color-text-secondary)",
    },
    blue: { bg: "var(--c-blue-50)", fg: "var(--c-blue-800)" },
    green: { bg: "var(--c-green-50)", fg: "var(--c-green-800)" },
    amber: { bg: "var(--c-amber-50)", fg: "var(--c-amber-800)" },
    red: { bg: "var(--c-red-50)", fg: "var(--c-red-800)" },
  };
  const { bg, fg } = baseFamily[variant];

  const style: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: bg,
    color: fg,
    padding: "var(--kuvio-pill-padding)",
    borderRadius: "var(--kuvio-pill-radius)",
    fontSize: 11,
    fontWeight: 500,
    fontFamily: "var(--font-sans)",
    lineHeight: 1.4,
    cursor: onClick ? "pointer" : "default",
    border: "none",
  };

  return (
    <span style={style} onClick={onClick}>
      {icon ? (
        <i
          className={`ti ti-${icon}`}
          style={{ fontSize: 11, verticalAlign: -1 }}
          aria-hidden="true"
        />
      ) : null}
      {children}
    </span>
  );
}

// ============================================================================
// <InheritanceChip>
// ============================================================================

export interface InheritanceChipProps {
  scopeLabel: string;
  itemCount: number;
  onClick?: () => void;
}

export function InheritanceChip({
  scopeLabel,
  itemCount,
  onClick,
}: InheritanceChipProps) {
  const style: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: "var(--color-background-secondary)",
    color: "var(--color-text-secondary)",
    padding: "3px 8px",
    borderRadius: "var(--kuvio-pill-radius)",
    fontSize: 11,
    fontWeight: 400,
    fontFamily: "var(--font-sans)",
    lineHeight: 1.4,
    cursor: onClick ? "pointer" : "default",
    border: "none",
    transition: "background 120ms ease",
  };

  return (
    <span
      style={style}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <i
        className="ti ti-corner-up-left"
        style={{ fontSize: 11, verticalAlign: -1, marginRight: 2 }}
        aria-hidden="true"
      />
      {scopeLabel} · {itemCount} {itemCount === 1 ? "instruction" : "instructions"}
    </span>
  );
}

// ============================================================================
// <MetricCard>
// ============================================================================

export interface MetricCardProps {
  label: string;
  value: ReactNode;
  sub?: string;
}

export function MetricCard({ label, value, sub }: MetricCardProps) {
  return (
    <div
      style={{
        padding: "var(--kuvio-metric-padding)",
        background: "var(--color-background-secondary)",
        borderRadius: "var(--kuvio-metric-radius)",
        fontFamily: "var(--font-sans)",
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: "var(--color-text-tertiary)",
          fontWeight: 500,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 500,
          color: "var(--color-text-primary)",
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.2,
        }}
      >
        {value}
      </div>
      {sub ? (
        <div
          style={{
            fontSize: 11,
            color: "var(--color-text-secondary)",
            marginTop: 2,
          }}
        >
          {sub}
        </div>
      ) : null}
    </div>
  );
}

// ============================================================================
// <Card>
// ============================================================================

export interface CardProps {
  children: ReactNode;
  padding?: string; // override default padding
  style?: CSSProperties;
}

export function Card({ children, padding, style }: CardProps) {
  return (
    <div
      style={{
        background: "var(--color-background-primary)",
        border: "var(--kuvio-border-default)",
        borderRadius: "var(--kuvio-card-radius)",
        padding: padding ?? "var(--kuvio-card-padding)",
        fontFamily: "var(--font-sans)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ============================================================================
// <EmptyState>
// ============================================================================

export interface EmptyStateProps {
  children: ReactNode;
}

export function EmptyState({ children }: EmptyStateProps) {
  return (
    <div
      style={{
        fontSize: 13,
        color: "var(--color-text-secondary)",
        fontFamily: "var(--font-sans)",
        padding: "1rem 0",
        textAlign: "center",
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

// ============================================================================
// <GhostAvatarRow> — Sprint 23.7
// ============================================================================
//
// A row of overlapping dashed "ghost" avatar circles in the brand ramp colours.
// The visual anchor for empty states (empty Workforce, welcome screen) — it
// reads as "a team, not yet filled". Pure decoration; no text, no interaction.

const GHOST_RAMP_DEFAULT = [
  "var(--c-purple-100)",
  "var(--c-teal-100)",
  "var(--c-coral-100)",
  "var(--c-amber-100)",
  "var(--c-blue-100)",
];

export interface GhostAvatarRowProps {
  /** How many ghost circles to render. Defaults to 5. */
  count?: number;
  /** Override the ramp colours (cycled if fewer than `count`). */
  ramps?: string[];
  /** Circle diameter in px. Defaults to 38. */
  size?: number;
}

export function GhostAvatarRow({ count = 5, ramps = GHOST_RAMP_DEFAULT, size = 38 }: GhostAvatarRowProps) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0 }} aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          style={{
            width: size,
            height: size,
            borderRadius: "50%",
            border: `1.5px dashed ${ramps[i % ramps.length]}`,
            background: "transparent",
            opacity: 0.55,
            marginLeft: i === 0 ? 0 : -8,
            flexShrink: 0,
          }}
        />
      ))}
    </div>
  );
}

// ============================================================================
// <Breadcrumb>
// ============================================================================

export interface BreadcrumbProps {
  segments: BreadcrumbSegment[];
}

export function Breadcrumb({ segments }: BreadcrumbProps) {
  return (
    <nav
      aria-label="Breadcrumb"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 12,
        color: "var(--color-text-tertiary)",
        fontFamily: "var(--font-sans)",
      }}
    >
      {segments.map((seg, idx) => {
        const isLast = idx === segments.length - 1;
        const segStyle: CSSProperties = {
          color: isLast
            ? "var(--color-text-primary)"
            : "var(--color-text-tertiary)",
          cursor: seg.onClick || seg.href ? "pointer" : "default",
          textDecoration: "none",
        };
        const inner = seg.href ? (
          <a href={seg.href} style={segStyle}>
            {seg.label}
          </a>
        ) : (
          <span style={segStyle} onClick={seg.onClick}>
            {seg.label}
          </span>
        );
        return (
          <React.Fragment key={idx}>
            {inner}
            {!isLast ? (
              <i
                className="ti ti-chevron-right"
                style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}
                aria-hidden="true"
              />
            ) : null}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

// ============================================================================
// <ScopeRow>
// ============================================================================

export interface ScopeRowProps {
  agent: AgentRef;
  selected?: boolean;
  status?: Status;
  statusLabel?: string;
  onClick?: () => void;
  /** Sprint 5u: node type — agents render a circular avatar, other types a square glyph. */
  type?: NodeKind;
  /** Shows the L-shaped connector stub at the row's vertical centre. */
  showConnector?: boolean;
}

export function ScopeRow({
  agent,
  selected = false,
  status = "idle",
  statusLabel,
  onClick,
  type = "agent",
  showConnector = true,
}: ScopeRowProps) {
  const isGray = agent.persona === "gray";
  const personaBg = selected
    ? isGray
      ? "var(--color-background-secondary)"
      : `var(--c-${agent.persona}-50)`
    : "transparent";
  const connectorColor = selected
    ? isGray
      ? "var(--color-border-secondary)"
      : `var(--c-${agent.persona}-600)`
    : "var(--color-border-tertiary)";

  const rowStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: showConnector ? "7px 10px 7px 20px" : "7px 10px",
    position: "relative",
    borderRadius: "var(--border-radius-md)",
    cursor: onClick ? "pointer" : "default",
    background: personaBg,
    fontFamily: "var(--font-sans)",
    transition: "background 120ms ease",
    minWidth: 0,
  };

  return (
    <div style={rowStyle} onClick={onClick}>
      {showConnector ? (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            left: 0,
            top: "50%",
            width: 16,
            height: 1,
            background: connectorColor,
          }}
        />
      ) : null}
      <NodeAvatar
        type={type}
        persona={type === "agent" ? agent.persona : undefined}
        size="md"
        initials={agent.initials}
        name={agent.name}
      />
      <span
        title={agent.name}
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: selected
            ? isGray
              ? "var(--color-text-primary)"
              : `var(--c-${agent.persona}-900)`
            : "var(--color-text-primary)",
          // Single-line with ellipsis: a long or unbreakable name (e.g. a
          // one-word folder) must shrink instead of overflowing the row and
          // colliding with the trailing status / count / memories controls.
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {agent.name}
      </span>
      {agent.role ? (
        <span
          title={agent.role}
          style={{
            fontSize: 12,
            color: selected
              ? isGray
                ? "var(--color-text-secondary)"
                : `var(--c-${agent.persona}-800)`
              : "var(--color-text-secondary)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
          }}
        >
          {agent.role}
        </span>
      ) : null}
      <span style={{ marginLeft: "auto", flexShrink: 0 }}>
        <StatusDot status={status} label={statusLabel} />
      </span>
    </div>
  );
}

// ============================================================================
// <FilterRowLabel> — Sprint 11
//
// A small left-aligned prefix for a filter chip row ("Surface:" / "Status:").
// Disambiguates which dimension a chip row filters without dominating the
// chrome. Not a header — a quiet prefix.
// ============================================================================

export function FilterRowLabel({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        fontSize: 11,
        fontStyle: "italic",
        color: "var(--color-text-tertiary)",
        marginRight: 8,
        flexShrink: 0,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

// ============================================================================
// <HostChip> — Sprint 14e caller-history indicator
// ============================================================================

export type HostKind =
  | "cowork"
  | "claude-web"
  | "claude-code"
  | "cursor"
  | "chatgpt"
  | "mcp-other";

const HOST_KIND_TO_SURFACE: Record<HostKind, Surface> = {
  cowork: "cowork",
  "claude-web": "claude-web",
  "claude-code": "claude-code",
  cursor: "cursor",
  chatgpt: "chatgpt",
  "mcp-other": "background",
};

export function HostChip({ kind }: { kind: HostKind | string | null | undefined }) {
  if (!kind) return null;
  // Sprint 16c (S16C.29) — wrapped-API materialisation source. `created_via` is
  // "wrapped_api:<slug>"; render a dedicated plug badge naming the wrapper.
  if (typeof kind === "string" && kind.startsWith("wrapped_api:")) {
    const slug = kind.slice("wrapped_api:".length) || "wrapped API";
    return (
      <span
        title={`Materialised from wrapped API "${slug}"`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 3,
          fontSize: 12,
          color: "var(--c-blue-800, var(--color-text-secondary))",
          background: "var(--c-blue-50, var(--color-background-secondary))",
          borderRadius: "var(--border-radius-sm)",
          padding: "1px 5px",
          whiteSpace: "nowrap",
        }}
      >
        <i className="ti ti-plug-connected" style={{ fontSize: 12 }} aria-hidden="true" />
        {slug}
      </span>
    );
  }
  const surface = HOST_KIND_TO_SURFACE[kind as HostKind] ?? "background";
  const meta = SURFACE_META[surface];
  return (
    <span
      title={meta.label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        fontSize: 12,
        color: meta.fgVar,
        background: meta.bgVar,
        borderRadius: "var(--border-radius-sm)",
        padding: "1px 5px",
        whiteSpace: "nowrap",
      }}
    >
      <i className={`ti ${meta.icon}`} style={{ fontSize: 12 }} aria-hidden="true" />
      {meta.label}
    </span>
  );
}

// ============================================================================
// <ConnectButton>
// ============================================================================

export interface ConnectButtonProps {
  agentId: string;
  surfaces?: Surface[];
  onConnect?: (surface: Surface) => void;
  hostStates?: Record<string, "never" | "idle" | "active">;
  lastSeenByHost?: Record<string, string>;
  /**
   * Sprint 16f — when the workspace exceeds the host-ingest threshold, the
   * dropdown surfaces scoped-endpoint recommendations (`/mcp/lite` + per-wrapper)
   * over the umbrella `/mcp`, and the button shows a warning badge if this session
   * is attached to the umbrella. Omit (or `exceedsThreshold:false`) for the
   * original Sprint 14e behaviour.
   */
  scale?: AttachmentPlan & { umbrellaAttached: boolean };
}

const DEFAULT_SURFACES: Surface[] = [
  "cowork",
  "cursor",
  "claude-code",
  "claude-web",
  "chatgpt",
];

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0 || ms < 60_000) return "just now";
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  return `${days}d ago`;
}

export function ConnectButton({
  agentId,
  surfaces = DEFAULT_SURFACES,
  onConnect,
  hostStates = {},
  lastSeenByHost = {},
  scale,
}: ConnectButtonProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: globalThis.MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  function handleSelect(surface: Surface) {
    setOpen(false);
    onConnect?.(surface);
  }

  // Sprint 16f — copy a scoped endpoint URL without closing the menu (the copy
  // buttons live inside `ref`, so the outside-click handler leaves the menu open).
  async function copyUrl(url: string, path: string) {
    try {
      await navigator.clipboard.writeText(url);
      showToast(`Copied ${path} URL`);
    } catch {
      showToast("Couldn't copy to clipboard", "error", true);
    }
  }

  const atScale = scale?.exceedsThreshold === true;
  const showWarningBadge = atScale && scale?.umbrellaAttached === true;

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button
        data-variant="primary"
        onClick={() => setOpen((v) => !v)}
        style={{
          fontSize: 13,
          padding: "8px 14px",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        data-agent-id={agentId}
      >
        <i
          className="ti ti-plug-connected"
          style={{ fontSize: 14 }}
          aria-hidden="true"
        />
        Connect
        {showWarningBadge ? (
          <i
            className="ti ti-alert-triangle"
            style={{ fontSize: 13, color: "var(--c-amber-400, #fbbf24)" }}
            aria-hidden="true"
            title="Tool list may be incomplete in this session — attach a scoped endpoint."
          />
        ) : null}
        <i
          className="ti ti-chevron-down"
          style={{ fontSize: 13 }}
          aria-hidden="true"
        />
      </button>
      {open ? (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            minWidth: atScale ? 320 : 220,
            maxWidth: 380,
            background: "var(--color-background-primary)",
            border: "var(--kuvio-border-default)",
            borderRadius: "var(--border-radius-md)",
            padding: 4,
            zIndex: 50,
            fontFamily: "var(--font-sans)",
          }}
        >
          {surfaces.map((surface) => {
            const meta = SURFACE_META[surface];
            const state = hostStates[surface] ?? "never";
            const lastSeen = lastSeenByHost[surface];
            return (
              <button
                key={surface}
                role="menuitem"
                onClick={() => handleSelect(surface)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  padding: "8px 10px",
                  border: "none",
                  background: "transparent",
                  fontSize: 13,
                  textAlign: "left",
                  borderRadius: "var(--border-radius-sm)",
                  color: "var(--color-text-primary)",
                  cursor: "pointer",
                }}
              >
                <i
                  className={`ti ${meta.icon}`}
                  style={{ fontSize: 14 }}
                  aria-hidden="true"
                />
                <span style={{ flex: 1 }}>{meta.label}</span>
                {state === "active" ? (
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "var(--kuvio-status-active)",
                      flexShrink: 0,
                    }}
                    title="Active now"
                  />
                ) : state === "idle" ? (
                  <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>
                    {formatRelative(lastSeen)}
                  </span>
                ) : null}
              </button>
            );
          })}
          {atScale && scale ? (
            <div style={{ borderTop: "var(--kuvio-border-default)", marginTop: 4, paddingTop: 6 }}>
              <div style={scaleWarnHint}>
                <i className="ti ti-alert-triangle" style={{ fontSize: 12 }} aria-hidden="true" />
                <span>
                  {scale.totalToolCount} tools — more than some hosts load at once. Attach a scoped
                  endpoint instead of the umbrella:
                </span>
              </div>
              <div style={scaleSectionLabel}>Recommended (scoped)</div>
              {scale.recommended.length > 0 ? (
                scale.recommended.map((ep) => (
                  <ScaleEndpointRow
                    key={ep.path}
                    label={ep.label}
                    path={ep.path}
                    toolCount={ep.toolCount}
                    reason={ep.reason}
                    onCopy={() => copyUrl(ep.url, ep.path)}
                  />
                ))
              ) : (
                <div style={{ ...scaleReason, padding: "2px 10px 6px" }}>
                  Set a credential on a wrapped API to get its isolated endpoint here.
                </div>
              )}
              {scale.umbrella ? (
                <>
                  <div style={scaleSectionLabel}>Advanced (everything)</div>
                  <ScaleEndpointRow
                    label={scale.umbrella.label}
                    path={scale.umbrella.path}
                    toolCount={scale.umbrella.tool_count}
                    reason="All tools — may exceed this host's ingest limit in a large workspace."
                    advanced
                    onCopy={() => copyUrl(scale.umbrella!.url, scale.umbrella!.path)}
                  />
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// Sprint 16f — one scoped/umbrella endpoint choice inside the Connect dropdown.
// Copy-URL only (v1); auto-attach is deferred to v1.1 (spec S16F.11).
function ScaleEndpointRow({
  label,
  path,
  toolCount,
  reason,
  advanced,
  onCopy,
}: {
  label: string;
  path: string;
  toolCount: number;
  reason: string;
  advanced?: boolean;
  onCopy: () => void;
}) {
  return (
    <div style={{ padding: "5px 10px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 12.5, fontWeight: 500, color: "var(--color-text-primary)" }}>{label}</span>
        <code style={scalePath}>{path}</code>
        <span style={{ flex: 1 }} />
        <span style={scaleCount} title={`${toolCount} tools`}>{toolCount}</span>
        <button type="button" onClick={onCopy} style={scaleCopyBtn} title={`Copy ${path} URL`}>
          <i className="ti ti-copy" style={{ fontSize: 12 }} aria-hidden="true" />
          Copy
        </button>
      </div>
      <div style={{ ...scaleReason, color: advanced ? "var(--c-amber-800, var(--color-text-secondary))" : "var(--color-text-secondary)" }}>
        {reason}
      </div>
    </div>
  );
}

const scaleWarnHint: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 6,
  padding: "4px 10px 6px",
  fontSize: 11.5,
  lineHeight: 1.45,
  color: "var(--c-amber-800, var(--color-text-secondary))",
};

const scaleSectionLabel: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "var(--color-text-tertiary)",
  padding: "6px 10px 2px",
};

const scalePath: CSSProperties = {
  fontSize: 11,
  fontFamily: "var(--font-mono, ui-monospace, monospace)",
  color: "var(--color-text-tertiary)",
};

const scaleCount: CSSProperties = {
  fontSize: 10.5,
  color: "var(--color-text-tertiary)",
  fontVariantNumeric: "tabular-nums",
  flexShrink: 0,
};

const scaleReason: CSSProperties = {
  fontSize: 11,
  lineHeight: 1.4,
  marginTop: 2,
};

const scaleCopyBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 11,
  padding: "3px 8px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-secondary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  flexShrink: 0,
  fontFamily: "var(--font-sans)",
};

// ============================================================================
// <ActivityFeedItem>
// ============================================================================

export interface ActivityFeedItemProps {
  agent: AgentRef;
  surface: Surface;
  body: ReactNode;
  timestamp: string; // relative or absolute, formatted by caller
  qualifier?: string; // e.g. "with David" or "via MCP"
  avatarSize?: AvatarSize;
}

export function ActivityFeedItem({
  agent,
  surface,
  body,
  timestamp,
  qualifier,
  avatarSize = "lg",
}: ActivityFeedItemProps) {
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        padding: "10px 0",
        fontFamily: "var(--font-sans)",
      }}
    >
      <Avatar
        persona={agent.persona}
        size={avatarSize}
        initials={agent.initials}
        name={agent.name}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 3,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 500 }}>{agent.name}</span>
          {agent.role ? (
            <span
              style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}
            >
              {agent.role}
            </span>
          ) : null}
          <SurfaceBadge surface={surface} />
        </div>
        <div
          style={{
            fontSize: 13,
            color: "var(--color-text-primary)",
            lineHeight: 1.5,
          }}
        >
          {body}
        </div>
        <div
          style={{
            fontSize: 11,
            color: "var(--color-text-tertiary)",
            marginTop: 3,
          }}
        >
          {timestamp}
          {qualifier ? ` · ${qualifier}` : ""}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// <ConfirmModal>
// ============================================================================

export interface ConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  message: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void | Promise<void>;
  variant?: "default" | "danger";
}

export function ConfirmModal({
  open,
  onOpenChange,
  title,
  message,
  confirmLabel,
  cancelLabel = "Cancel",
  onConfirm,
  variant = "default",
}: ConfirmModalProps) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);

  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement;
      setTimeout(() => dialogRef.current?.focus(), 0);
    } else {
      setPending(false);
      setError(null);
      if (triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus();
      }
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onOpenChange(false);
      }
      if (e.key === "Tab" && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onOpenChange]);

  if (!open) return null;

  async function handleConfirm() {
    setPending(true);
    setError(null);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Something went wrong");
      setPending(false);
    }
  }

  const overlay: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
    fontFamily: "var(--font-sans)",
  };

  const box: CSSProperties = {
    background: "var(--color-background-primary)",
    border: "0.5px solid var(--color-border-secondary)",
    borderRadius: "var(--border-radius-lg)",
    maxWidth: 420,
    width: "100%",
    padding: "24px",
  };

  const btnBase: CSSProperties = {
    fontSize: 13,
    padding: "8px 16px",
    borderRadius: "var(--border-radius-md)",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
    fontWeight: 500,
    border: "none",
  };

  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onOpenChange(false); }}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        tabIndex={-1}
        style={box}
      >
        <h2
          id="confirm-modal-title"
          style={{ fontSize: 18, fontWeight: 500, margin: "0 0 8px 0", color: "var(--color-text-primary)" }}
        >
          {title}
        </h2>
        <div style={{ fontSize: 14, color: "var(--color-text-secondary)", lineHeight: 1.5, marginBottom: 20 }}>
          {message}
        </div>
        {error && (
          <div style={{ fontSize: 13, color: "var(--c-red-600)", marginBottom: 12 }}>{error}</div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            onClick={() => onOpenChange(false)}
            style={{ ...btnBase, background: "transparent", color: "var(--color-text-secondary)" }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={handleConfirm}
            disabled={pending}
            style={{
              ...btnBase,
              background: variant === "danger" ? "var(--c-red-600)" : "var(--color-text-primary)",
              color: "#fff",
              opacity: pending ? 0.7 : 1,
            }}
          >
            {pending && (
              <span style={{ display: "inline-block", width: 12, height: 12, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.6s linear infinite", marginRight: 6, verticalAlign: -1 }} />
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// <EditableTitle>
// ============================================================================

export interface EditableTitleProps {
  value: string;
  onSave: (newValue: string) => Promise<void>;
  placeholder?: string;
  maxLength?: number;
  fontSize?: number;
  fontWeight?: number;
}

export function EditableTitle({
  value,
  onSave,
  placeholder = "Untitled",
  maxLength = 80,
  fontSize = 22,
  fontWeight = 500,
}: EditableTitleProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [hovered, setHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  async function commit() {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === value) {
      setEditing(false);
      setDraft(value);
      return;
    }
    setSaving(true);
    try {
      await onSave(trimmed);
      setEditing(false);
    } catch {
      inputRef.current?.focus();
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    const inputStyle: CSSProperties = {
      fontSize,
      fontWeight,
      fontFamily: "var(--font-sans)",
      color: "var(--color-text-primary)",
      background: "transparent",
      border: "none",
      borderBottom: "0.5px solid var(--color-border-secondary)",
      outline: "none",
      padding: "0 0 2px 0",
      margin: 0,
      width: "100%",
      lineHeight: 1.3,
    };

    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Escape") { setEditing(false); setDraft(value); }
          }}
          onBlur={commit}
          disabled={saving}
          maxLength={maxLength}
          placeholder={placeholder}
          style={inputStyle}
          aria-label="Rename"
        />
        {saving && (
          <span style={{
            display: "inline-block", width: 14, height: 14,
            border: "2px solid var(--color-border-secondary)",
            borderTopColor: "var(--color-text-secondary)",
            borderRadius: "50%", animation: "spin 0.6s linear infinite", flexShrink: 0,
          }} />
        )}
      </span>
    );
  }

  const displayStyle: CSSProperties = {
    fontSize,
    fontWeight,
    fontFamily: "var(--font-sans)",
    color: "var(--color-text-primary)",
    margin: 0,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "0 4px",
    borderRadius: "var(--border-radius-sm)",
    background: hovered ? "var(--color-background-secondary)" : "transparent",
    transition: "background 120ms ease",
    lineHeight: 1.3,
  };

  return (
    <span
      style={displayStyle}
      onClick={() => setEditing(true)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && setEditing(true)}
      title="Click to rename"
    >
      {value}
      <i
        className="ti ti-pencil"
        style={{ fontSize: 14, color: "var(--color-text-tertiary)", opacity: hovered ? 1 : 0, transition: "opacity 120ms ease" }}
        aria-hidden="true"
      />
    </span>
  );
}

// ============================================================================
// <Toast> + useToast
// ============================================================================

/** Sprint 14g: an optional action button (e.g. "Undo") on a toast. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastItem {
  id: number;
  message: string;
  variant: "success" | "error" | "info";
  sticky?: boolean;
  action?: ToastAction;
  /** Auto-dismiss delay (non-sticky only). Defaults to 3000ms (5000ms with an action). */
  durationMs?: number;
  /**
   * Sprint 24.12 — optional caller-supplied id for de-duplication. A second
   * toast with the same `stableId` REPLACES the one on screen (atomic swap, no
   * stacking) — e.g. a repeated "Connected to M365".
   */
  stableId?: string;
  /**
   * Sprint 24.17a — rich "pickup" toast fields (an agent picked up work). When
   * `iconGlyph` is set the row renders an avatar icon container + title +
   * `subtitle` instead of the plain status glyph; when `progress` (0–100) is set
   * it renders a phase-progress bar (hidden at 100) in place of the sticky
   * countdown. All optional — ordinary toasts ignore them.
   */
  subtitle?: string;
  progress?: number;
  iconGlyph?: string;
  iconRampHex?: string;
}

const toastListeners: Set<(t: ToastItem) => void> = new Set();
let toastId = 0;

/**
 * Sprint 24.12 — every toast self-dismisses: non-sticky after `durationMs`
 * (default 3000ms, 5000ms when it carries an action so it stays clickable), and
 * even `sticky` toasts after a hard 60s ceiling so an unattended browser self-
 * cleans. Pass `id` to de-duplicate (replace-in-place rather than stacking).
 */
export function showToast(
  message: string,
  variant: "success" | "error" | "info" = "success",
  sticky = false,
  action?: ToastAction,
  durationMs?: number,
  id?: string,
) {
  const item: ToastItem = { id: ++toastId, message, variant, sticky, action, durationMs, stableId: id };
  toastListeners.forEach((fn) => fn(item));
}

/**
 * Sprint 24.17a — the rich agent-pickup toast (info, sticky, avatar + subtitle +
 * phase-progress bar). Reuses the Sprint 24.12 toast machinery (de-dup by
 * `stableId`, 60s ceiling, dismiss). Fired by the pickup-toast observer.
 */
export interface PickupToastInput {
  stableId: string;
  title: string;
  subtitle?: string;
  /** 0–100; the progress bar hides at 100. */
  progress?: number;
  iconGlyph?: string;
  iconRampHex?: string;
}

export function showPickupToast(input: PickupToastInput) {
  const item: ToastItem = {
    id: ++toastId,
    message: input.title,
    variant: "info",
    sticky: true,
    stableId: input.stableId,
    subtitle: input.subtitle,
    progress: input.progress,
    iconGlyph: input.iconGlyph,
    iconRampHex: input.iconRampHex,
  };
  toastListeners.forEach((fn) => fn(item));
}

/** Hard ceiling for sticky toasts — long enough to read + click, short enough to self-clean. */
const STICKY_CEILING_MS = 60_000;

function toastDuration(t: ToastItem): number {
  if (t.sticky) return STICKY_CEILING_MS;
  return t.durationMs ?? (t.action ? 5000 : 3000);
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => {
    function onToast(t: ToastItem) {
      setToasts((prev) => {
        // Replace-on-id (Sprint 24.12) — swap an existing toast in place so a
        // repeat doesn't stack. The row is keyed by stableId, so this updates
        // props without remounting; the fresh numeric `id` restarts its timer.
        if (t.stableId) {
          const idx = prev.findIndex((x) => x.stableId === t.stableId);
          if (idx >= 0) {
            const next = prev.slice();
            next[idx] = t;
            return next;
          }
        }
        return [...prev, t];
      });
    }
    toastListeners.add(onToast);
    return () => { toastListeners.delete(onToast); };
  }, []);

  const dismiss = (id: number) => setToasts((prev) => prev.filter((x) => x.id !== id));

  if (toasts.length === 0) return null;

  return (
    <div style={toastContainerStyle}>
      {toasts.map((t) => (
        <ToastRow key={t.stableId ?? String(t.id)} toast={t} onDismiss={dismiss} />
      ))}
    </div>
  );
}

const toastContainerStyle: CSSProperties = {
  position: "fixed",
  bottom: 20,
  right: 20,
  zIndex: 9999,
  display: "flex",
  flexDirection: "column",
  gap: 8,
  fontFamily: "var(--font-sans)",
};

/**
 * Sprint 24.12 — one toast row. Owns its auto-dismiss timer (paused while
 * hovered, so a reader isn't cut off), a universal dismiss button, and — for
 * sticky toasts — a thin progress bar counting down the 60s ceiling.
 */
function ToastRow({ toast: t, onDismiss }: { toast: ToastItem; onDismiss: (id: number) => void }) {
  const [paused, setPaused] = useState(false);
  const duration = toastDuration(t);

  // Restart on (re)mount, unpause, or replace (new numeric id). Pausing clears it.
  useEffect(() => {
    if (paused) return;
    const timer = setTimeout(() => onDismiss(t.id), duration);
    return () => clearTimeout(timer);
  }, [paused, duration, t.id, onDismiss]);

  const accent =
    t.variant === "error" ? "var(--color-text-danger)"
    : t.variant === "info" ? "var(--color-text-info)"
    : "var(--color-text-success)";
  const rich = !!t.iconGlyph; // Sprint 24.17a pickup toast layout
  const showPhaseBar = typeof t.progress === "number" && t.progress < 100;

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        position: "relative",
        overflow: "hidden",
        padding: rich ? "12px 30px 12px 14px" : "10px 30px 10px 16px",
        borderRadius: "var(--border-radius-md)",
        border: "0.5px solid var(--color-border-secondary)",
        borderLeft: `3px solid ${accent}`,
        background: "var(--color-background-primary)",
        color: t.variant === "error" ? "var(--c-red-600)" : "var(--color-text-primary)",
        fontSize: 13,
        fontWeight: 400,
        display: "flex",
        alignItems: rich ? "flex-start" : "center",
        gap: rich ? 10 : 8,
        minWidth: rich ? 320 : 240,
        maxWidth: rich ? 420 : 380,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      }}
    >
      {rich ? (
        <span style={toastAvatar(t.iconRampHex)} aria-hidden="true">
          <i className={`ti ${t.iconGlyph}`} style={{ fontSize: 15, color: t.iconRampHex ?? accent, lineHeight: 1 }} />
        </span>
      ) : (
        <i
          className={`ti ti-${t.variant === "error" ? "alert-circle" : t.variant === "info" ? "info-circle" : "check"}`}
          style={{ fontSize: 14, flexShrink: 0, color: accent }}
          aria-hidden="true"
        />
      )}

      {rich ? (
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>{t.message}</span>
          {t.subtitle && <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{t.subtitle}</span>}
        </div>
      ) : (
        <span style={{ flex: 1 }}>{t.message}</span>
      )}

      {t.action && (
        <button
          onClick={() => { t.action!.onClick(); onDismiss(t.id); }}
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "var(--color-accent, var(--color-text-primary))",
            padding: 0,
            fontSize: 13,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {t.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={() => onDismiss(t.id)}
        title="Dismiss"
        aria-label="Dismiss"
        style={{
          position: "absolute",
          top: 6,
          right: 6,
          border: "none",
          background: "transparent",
          cursor: "pointer",
          color: "var(--color-text-tertiary)",
          padding: 0,
          display: "flex",
          lineHeight: 0,
          opacity: 0.7,
        }}
      >
        <i className="ti ti-x" style={{ fontSize: 12 }} aria-hidden="true" />
      </button>
      {showPhaseBar ? (
        <ToastPhaseBar progress={t.progress!} />
      ) : (
        t.sticky && !paused && !rich && <ToastProgressBar key={t.id} duration={duration} />
      )}
    </div>
  );
}

/** Sprint 24.17a — the pickup toast's avatar icon container (ramp-tinted). */
function toastAvatar(rampHex?: string): CSSProperties {
  const hex = rampHex ?? "#7F77DD";
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  let bg = "var(--color-background-secondary)";
  if (m) {
    const int = parseInt(m[1]!, 16);
    bg = `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, 0.14)`;
  }
  return {
    width: 28,
    height: 28,
    flexShrink: 0,
    borderRadius: "50%",
    background: bg,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };
}

/** Sprint 24.17a — phase-progress bar (audit→draft→questions); width = `progress`%. */
function ToastPhaseBar({ progress }: { progress: number }) {
  return (
    <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 2, background: "var(--color-border-tertiary)" }}>
      <div
        style={{
          height: "100%",
          width: `${Math.max(0, Math.min(100, progress))}%`,
          background: "var(--color-text-info)",
          transition: "width 400ms ease",
        }}
      />
    </div>
  );
}

/** A 2px countdown strip flush with the toast's bottom edge (sticky toasts only). */
function ToastProgressBar({ duration }: { duration: number }) {
  const [w, setW] = useState(100);
  useEffect(() => {
    // Let the 100% width paint first, then transition to 0 over `duration`.
    const id = setTimeout(() => setW(0), 20);
    return () => clearTimeout(id);
  }, []);
  return (
    <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 2, background: "rgba(127,127,127,0.15)" }}>
      <div
        style={{
          height: "100%",
          width: `${w}%`,
          background: "var(--color-text-tertiary)",
          transition: `width ${duration}ms linear`,
        }}
      />
    </div>
  );
}

// ============================================================================
// CopyableCode — an inline code chip with a one-click copy button.
// Reusable anywhere an exact string needs to be copied (e.g. the type-the-name
// value in delete-confirmation dialogs). Shows a transient check on success.
// ============================================================================

const copyableCodeStyle: CSSProperties = {
  fontFamily: "var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)",
  fontSize: "0.9em",
  padding: "1px 6px",
  borderRadius: 4,
  background: "var(--color-background-secondary, rgba(0,0,0,0.05))",
  border: "0.5px solid var(--color-border-secondary)",
  color: "var(--color-text-primary)",
  wordBreak: "break-all",
};

const copyableBtnStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  padding: 2,
  borderRadius: 4,
  lineHeight: 0,
  flexShrink: 0,
};

export function CopyableCode({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, verticalAlign: "middle" }}>
      <code style={copyableCodeStyle}>{text}</code>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          navigator.clipboard?.writeText(text).then(
            () => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1200);
            },
            () => showToast("Couldn't copy to clipboard", "error"),
          );
        }}
        title={copied ? "Copied" : label ?? "Copy"}
        aria-label={`Copy "${text}"`}
        style={copyableBtnStyle}
      >
        <i
          className={`ti ti-${copied ? "check" : "copy"}`}
          style={{ fontSize: 12, color: copied ? "var(--c-teal-600, #0d9488)" : "var(--color-text-tertiary)" }}
          aria-hidden="true"
        />
      </button>
    </span>
  );
}

// ============================================================================
// Default export (handy for batched imports)
// ============================================================================

export default {
  Avatar,
  NodeAvatar,
  SurfaceBadge,
  StatusDot,
  Pill,
  InheritanceChip,
  MetricCard,
  Card,
  EmptyState,
  Breadcrumb,
  ScopeRow,
  ConnectButton,
  ActivityFeedItem,
  ConfirmModal,
  EditableTitle,
  ToastContainer,
};
